/*
  De La Terre · Header Runtime v1 · Fase 4.2
  Runtime idempotente para wishlist, feedback do carrinho e limpeza de estado legado.
  A configuração sticky/hide-on-scroll permanece server-rendered no Liquid.
*/
(() => {
  if (window.__dltHeaderRuntimeV1Installed) {
    window.DLTHeaderRuntimeV1?.scan?.(document);
    return;
  }

  window.__dltHeaderRuntimeV1Installed = true;

  const states = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const listen = (state, target, eventName, handler, options = {}) => {
    if (!target) return;
    target.addEventListener(eventName, handler, {
      ...options,
      signal: state.controller.signal
    });
  };

  const safeWishlist = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem('dlt-wishlist') || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const getWishlistCount = () => {
    const uniqueItems = new Set();

    safeWishlist().forEach((item) => {
      if (item === null || item === undefined) return;

      if (typeof item === 'string' || typeof item === 'number') {
        uniqueItems.add(String(item));
        return;
      }

      const key = item.handle || item.id || item.productId || item.product_id;
      if (key) uniqueItems.add(String(key));
    });

    return uniqueItems.size;
  };

  const pulse = (element) => {
    if (!element || reducedMotion.matches || typeof element.animate !== 'function') return;

    element.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.06)', offset: 0.42 },
        { transform: 'scale(1)' }
      ],
      {
        duration: 360,
        easing: 'ease',
        fill: 'none'
      }
    );
  };

  const clearLegacyHeaderState = (headerSection, storeHeader) => {
    if (!storeHeader) return;

    [
      'is-dlt-scrolled',
      'is-dlt-home-reveal-active',
      'is-dlt-home-header-visible',
      'is-dlt-home-header-hidden',
      'dlt-home-header-guard-ready'
    ].forEach((className) => headerSection.classList.remove(className));

    headerSection.removeAttribute('data-dlt-home-header-state');

    ['opacity', 'visibility', 'pointer-events', 'will-change'].forEach((property) => {
      storeHeader.style.removeProperty(property);
    });

    if (window.scrollY < 12 && storeHeader.style.getPropertyValue('transform')) {
      storeHeader.style.removeProperty('transform');
    }
  };

  const destroy = (headerSection) => {
    const state = states.get(headerSection);
    if (!state) return;

    state.controller.abort();
    states.delete(headerSection);
    delete headerSection.dataset.dltHeaderReady;
  };

  const init = (headerSection) => {
    if (!(headerSection instanceof Element)) return;

    destroy(headerSection);

    const state = {
      controller: new AbortController(),
      storeHeader: headerSection.querySelector('[data-dlt-store-header]'),
      wishlistLink: headerSection.querySelector('.dlt-header__wishlist-link'),
      wishlistCount: headerSection.querySelector('[data-dlt-wishlist-count]'),
      cartLink: headerSection.querySelector('.dlt-header__cart-link')
    };

    states.set(headerSection, state);
    headerSection.dataset.dltHeaderReady = 'true';

    const syncWishlistCount = () => {
      if (!state.wishlistCount) return;

      const count = getWishlistCount();
      state.wishlistCount.textContent = String(count);
      state.wishlistCount.hidden = count < 1;

      if (!state.wishlistLink) return;

      state.wishlistLink.setAttribute(
        'aria-label',
        count > 0
          ? `Abrir lista de desejos com ${count} ${count === 1 ? 'item' : 'itens'}`
          : 'Abrir lista de desejos'
      );
    };

    const handleStorage = (event) => {
      if (event.key === 'dlt-wishlist') syncWishlistCount();
    };

    const handleWishlistUpdate = () => {
      syncWishlistCount();
      pulse(state.wishlistLink);
    };

    const handleCartUpdate = () => pulse(state.cartLink);

    clearLegacyHeaderState(headerSection, state.storeHeader);
    syncWishlistCount();

    listen(state, window, 'storage', handleStorage);
    listen(state, window, 'dlt:wishlist-updated', handleWishlistUpdate);
    listen(state, document, 'cart:change', handleCartUpdate, { passive: true });
    listen(state, document, 'product:added', handleCartUpdate, { passive: true });
    listen(state, window, 'cart-update', handleCartUpdate, { passive: true });
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches('.shopify-section--header')) {
      init(root);
      return;
    }

    root.querySelectorAll?.('.shopify-section--header').forEach(init);
  };

  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof Element && event.target.matches('.shopify-section--header')) {
      destroy(event.target);
    }
  });

  window.DLTHeaderRuntimeV1 = { scan, init, destroy };
  scan(document);
})();
/*
  De La Terre · Header Markets v1
  Selects wide, compact or drawer mode from the header's real rendered width.
*/
(() => {
  if (window.__dltHeaderMarketsV1Installed) return;
  window.__dltHeaderMarketsV1Installed = true;

  const states = new WeakMap();

  const setFit = (state, mode) => {
    state.section.classList.toggle('dlt-header-fit--compact', mode === 'compact');
    state.section.classList.toggle('dlt-header-fit--drawer', mode === 'drawer');
    state.storeHeader?.setAttribute('data-dlt-header-fit', mode);
  };

  const hasOverflow = (state) => {
    if (!state.wrapper || !state.mainNav || !state.secondaryNav || !state.mainLinks) return false;

    const mainBox = state.mainNav.getBoundingClientRect();
    const secondaryBox = state.secondaryNav.getBoundingClientRect();
    const linksOverflow = state.mainLinks.scrollWidth > state.mainNav.clientWidth + 1;
    const regionsOverlap = mainBox.right > secondaryBox.left - 6;
    const wrapperOverflow = state.wrapper.scrollWidth > state.wrapper.clientWidth + 1;

    return linksOverflow || regionsOverlap || wrapperOverflow;
  };

  const sync = (state, force = false) => {
    if (!state.storeHeader || state.controller.signal.aborted) return;

    if (window.innerWidth < 1150) {
      state.lastWidth = window.innerWidth;
      setFit(state, 'mobile');
      return;
    }

    if (!force && state.lastWidth === window.innerWidth) return;
    state.lastWidth = window.innerWidth;

    setFit(state, 'wide');
    void state.wrapper?.offsetWidth;

    if (!hasOverflow(state)) return;

    setFit(state, 'compact');
    void state.wrapper?.offsetWidth;

    if (hasOverflow(state)) setFit(state, 'drawer');
  };

  const schedule = (state, force = false) => {
    if (state.controller.signal.aborted) return;
    if (state.frame) cancelAnimationFrame(state.frame);

    state.frame = requestAnimationFrame(() => {
      state.frame = 0;
      sync(state, force);
    });
  };

  const destroy = (section) => {
    const state = states.get(section);
    if (!state) return;

    state.controller.abort();
    if (state.frame) cancelAnimationFrame(state.frame);
    states.delete(section);
  };

  const init = (section) => {
    if (!(section instanceof Element)) return;
    destroy(section);

    const state = {
      section,
      controller: new AbortController(),
      frame: 0,
      lastWidth: 0,
      storeHeader: section.querySelector('[data-dlt-store-header]'),
      wrapper: section.querySelector('.header__wrapper'),
      mainNav: section.querySelector('.header__main-nav'),
      mainLinks: section.querySelector('.header__main-nav .dlt-header__link-list'),
      secondaryNav: section.querySelector('.header__secondary-nav')
    };

    if (!state.storeHeader) return;
    states.set(section, state);

    window.addEventListener('resize', () => schedule(state), {
      passive: true,
      signal: state.controller.signal
    });

    schedule(state, true);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => schedule(state, true));
    }
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches('.shopify-section--header')) {
      init(root);
      return;
    }

    root.querySelectorAll?.('.shopify-section--header').forEach(init);
  };

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  window.DLTHeaderMarketsV1 = { scan, init, destroy };
  scan(document);
})();
/*
  De La Terre · Header EA Bridge Loader v1.1 · H0.6
  Evita polling continuo: faz poucas tentativas espaçadas e mantém retry por
  interação. Assim que o bridge carrega, remove listeners/timers do loader.
*/
(() => {
  if (window.__dltHeaderEaLoaderV1Installed) return;
  window.__dltHeaderEaLoaderV1Installed = true;

  const currentScript = document.currentScript;
  const bridgeUrl = currentScript?.dataset?.bridge;
  if (!bridgeUrl) return;

  const localeSelector = '.localization-selector--locale';
  const eaSourceSelector =
    '#ea-auto-translate-root select.eat-langSelectorSelect[name="eaAtLanguages"], ' +
    '#ea-auto-translate-root select#eaAtLanguages';

  if (!document.querySelector(localeSelector)) return;

  const controller = new AbortController();
  const retryDelays = [650, 1400, 3000, 6000];
  let retryIndex = 0;
  let timer = 0;
  let loading = false;
  let finished = false;

  const clearRetry = () => {
    if (!timer) return;
    window.clearTimeout(timer);
    timer = 0;
  };

  const cleanup = () => {
    if (finished) return;
    finished = true;
    clearRetry();
    controller.abort();
  };

  const scheduleRetry = () => {
    if (finished || loading || timer || retryIndex >= retryDelays.length) return;

    const delay = retryDelays[retryIndex++];
    timer = window.setTimeout(() => {
      timer = 0;
      if (!loadBridgeIfReady()) scheduleRetry();
    }, delay);
  };

  const loadBridgeIfReady = () => {
    if (window.__dltEaTranslateMenuInstalled) {
      cleanup();
      return true;
    }

    if (finished || loading) return loading;
    if (!document.querySelector(eaSourceSelector)) return false;

    loading = true;
    clearRetry();

    const script = document.createElement('script');
    script.src = bridgeUrl;
    script.async = true;
    script.dataset.dltEaBridge = 'true';
    script.addEventListener('load', cleanup, { once: true });
    script.addEventListener('error', () => {
      loading = false;
      scheduleRetry();
    }, { once: true });
    document.head.append(script);
    return true;
  };

  const retryFromInteraction = (event) => {
    const target = event.target instanceof Element
      ? event.target.closest(localeSelector)
      : null;

    if (target && !loadBridgeIfReady()) scheduleRetry();
  };

  document.addEventListener('pointerdown', retryFromInteraction, {
    capture: true,
    passive: true,
    signal: controller.signal
  });
  document.addEventListener('focusin', retryFromInteraction, {
    capture: true,
    signal: controller.signal
  });

  window.addEventListener('pagehide', cleanup, {
    once: true,
    signal: controller.signal
  });

  if (!loadBridgeIfReady()) scheduleRetry();
})();

/* De La Terre · Header Scroll Direction v2 */
(() => {
  if (window.__dltHeaderScrollDirectionV2Installed) return;
  window.__dltHeaderScrollDirectionV2Installed = true;

  const states = new WeakMap();
  const mediaReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  const hasOpenInteraction = (section) => {
    if (section.matches(':focus-within')) return true;
    if (section.querySelector('details[open], [aria-expanded="true"]')) return true;
    return Boolean(document.querySelector('dialog[open], [aria-modal="true"][open], cart-drawer[open], navigation-drawer[open]'));
  };

  const publishOffset = (state) => {
    const section = state.section;
    const topValue = Number.parseFloat(getComputedStyle(section).top) || 0;
    const offset = state.hidden ? 0 : Math.max(0, Math.round(section.offsetHeight + topValue));
    document.documentElement.style.setProperty('--dlt-active-header-offset', `${offset}px`);
    window.dispatchEvent(new CustomEvent('dlt:header-visibility', {
      detail: { visible: !state.hidden, offset }
    }));
  };

  const setHidden = (state, hidden, force = false) => {
    if (!force && state.hidden === hidden) return;
    state.hidden = hidden;
    state.section.classList.toggle('dlt-header-scroll--hidden', hidden);
    state.section.classList.toggle('dlt-header-scroll--visible', !hidden);
    state.section.dataset.dltHeaderScrollState = hidden ? 'hidden' : 'visible';
    publishOffset(state);
  };

  const sync = (state) => {
    state.frame = 0;
    if (state.controller.signal.aborted) return;

    const y = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
    const delta = y - state.lastY;
    const stickyEnabled = state.storeHeader?.hasAttribute('sticky');
    const hideEnabled = state.storeHeader?.dataset.dltHideOnScroll === 'true';
    const topZone = y <= 16;

    if (!stickyEnabled || !hideEnabled || topZone || hasOpenInteraction(state.section)) {
      setHidden(state, false);
      state.accumulator = 0;
      state.lastY = y;
      return;
    }

    if (Math.abs(delta) < 2) return;

    const direction = delta > 0 ? 1 : -1;
    if (direction !== state.direction) {
      state.direction = direction;
      state.accumulator = 0;
    }
    state.accumulator += Math.abs(delta);

    const headerHeight = Math.max(48, state.section.offsetHeight);
    if (direction > 0 && y > headerHeight + 28 && state.accumulator >= 12) {
      setHidden(state, true);
      state.accumulator = 0;
    } else if (direction < 0 && state.accumulator >= 7) {
      setHidden(state, false);
      state.accumulator = 0;
    }

    state.lastY = y;
  };

  const schedule = (state) => {
    if (state.frame || state.controller.signal.aborted) return;
    state.frame = requestAnimationFrame(() => sync(state));
  };

  const destroy = (section) => {
    const state = states.get(section);
    if (!state) return;
    state.controller.abort();
    state.observer?.disconnect();
    if (state.frame) cancelAnimationFrame(state.frame);
    section.classList.remove('dlt-header-scroll--hidden', 'dlt-header-scroll--visible');
    section.removeAttribute('data-dlt-header-scroll-state');
    section.removeAttribute('data-dlt-scroll-direction-ready');
    states.delete(section);
  };

  const init = (section) => {
    if (!(section instanceof Element)) return;
    destroy(section);

    const storeHeader = section.querySelector('[data-dlt-store-header]');
    if (!storeHeader) return;

    const state = {
      section,
      storeHeader,
      controller: new AbortController(),
      observer: null,
      frame: 0,
      hidden: false,
      lastY: Math.max(0, window.scrollY || 0),
      direction: 0,
      accumulator: 0
    };
    states.set(section, state);
    section.dataset.dltScrollDirectionReady = 'true';
    setHidden(state, false, true);

    const options = { passive: true, signal: state.controller.signal };
    window.addEventListener('scroll', () => schedule(state), options);
    window.addEventListener('resize', () => {
      setHidden(state, false);
      schedule(state);
    }, options);
    section.addEventListener('focusin', () => setHidden(state, false), { signal: state.controller.signal });
    section.addEventListener('pointerenter', () => {
      if (window.scrollY > 16) setHidden(state, false);
    }, { passive: true, signal: state.controller.signal });

    state.observer = new MutationObserver(() => {
      if (hasOpenInteraction(section)) setHidden(state, false);
    });
    state.observer.observe(section, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'aria-expanded']
    });

    mediaReduce.addEventListener?.('change', () => publishOffset(state), { signal: state.controller.signal });
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches('.shopify-section--header')) init(root);
    root.querySelectorAll?.('.shopify-section--header').forEach(init);
  };

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof Element && event.target.matches('.shopify-section--header')) destroy(event.target);
  });

  scan(document);
})();
